/**
 * Canlı harita — saf kurallar. Restoran panelindeki canlı kurye ekranıyla (partner
 * courier/live) AYNI palet, aynı "son konum" cümlesi ve aynı bekleme süresi.
 */

/**
 * KURYE RENGİ — her kuryenin TEK rengi var: haritadaki işareti, listedeki satırı, taşıdığı
 * paketlerin çipleri ve teslim noktaları hep o renktir ("hangi paket kimde" renkten okunsun).
 * Konum tazeliğinin yeşil/sarı/kırmızısı palette BİLEREK yok ki ikisi karışmasın.
 */
export const COURIER_COLORS = ['#2563eb', '#9333ea', '#f97316', '#db2777', '#65a30d', '#a16207', '#0f766e', '#7c3aed', '#be185d', '#475569'];

/** Kurye → renk eşlemesi tarayıcıda hatırlanır. */
export const COLOR_STORAGE_KEY = 'flm.courierColors';

/** Saklanan eşlemeden yalnız geçerli (paletteki) renkleri alır. */
export function sanitizeCourierColors(raw) {
    const out = {};
    if (raw && typeof raw === 'object') {
        Object.entries(raw).forEach(([id, col]) => {
            if (typeof col === 'string' && COURIER_COLORS.includes(col)) out[id] = col;
        });
    }
    return out;
}

/**
 * Rengi olmayan kuryelere, o an haritadaki kuryelerde EN AZ kullanılan rengi verir. Bilinen
 * eşleme değişmez (kurye yarın da aynı renkte gelir). Yeni atama yoksa AYNI nesne döner.
 */
export function assignCourierColors(known, courierIds) {
    let next = known;
    courierIds.forEach((id) => {
        if (next[id]) return;
        const usage = new Map(COURIER_COLORS.map((c) => [c, 0]));
        courierIds.forEach((other) => {
            const col = next[other];
            if (col) usage.set(col, (usage.get(col) || 0) + 1);
        });
        const picked = COURIER_COLORS.reduce(
            (best, c) => ((usage.get(c) || 0) < (usage.get(best) || 0) ? c : best),
            COURIER_COLORS[0],
        );
        if (next === known) next = { ...known };
        next[id] = picked;
    });
    return next;
}

const pad = (n) => String(n).padStart(2, '0');

/** Aynı gün "10:42", başka gün "06.10 23:15" (yerel saat). */
export function lastLocationClock(iso, now = new Date()) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const clock = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    return sameDay ? clock : `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${clock}`;
}

/** "5 dk önce" / "az önce" / "2 sa 10 dk önce". */
export function lastLocationAge(iso, now = new Date()) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const min = Math.max(0, Math.floor((now.getTime() - t) / 60000));
    if (min < 1) return 'az önce';
    if (min < 60) return `${min} dk önce`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h} sa önce` : `${h} sa ${m} dk önce`;
}

/** Kartta gösterilen cümle. Konum hiç gelmediyse bunu açıkça söyler. */
export function lastLocationText(iso, now = new Date()) {
    const clock = lastLocationClock(iso, now);
    const age = lastLocationAge(iso, now);
    return clock && age ? `Son konum ${clock}, ${age}` : 'Konum hiç gelmedi';
}

/** Aynı kuryeye art arda bildirim gitmesin: bir istekten sonra bu süre boyunca düğme kapalıdır. */
export const LOCATION_REQUEST_COOLDOWN_MS = 60000;

export function locationRequestCoolingDown(lastRequestedAt, nowMs) {
    return lastRequestedAt != null && nowMs - lastRequestedAt < LOCATION_REQUEST_COOLDOWN_MS;
}

/** Paket hâlâ restoranda mı (kuryeye atanmış ama teslim alınmamış). "Yola çıkar / kuryeden al" yalnız bunlarda. */
export const isAtRestaurant = (o) => o?.isPickedUp === false;

/** Paketin kısa adı: sipariş numarası; pazaryeri dışı siparişte numara uzun bir kimlikse kısaltılır. */
export function orderShortNo(o) {
    const no = String(o?.orderNo || '').trim();
    if (!no) return '—';
    return no.length > 12 ? `…${no.slice(-6)}` : no;
}

/** "Köz Köze · #1042" */
export const orderLabel = (o) => `${o?.partnerName || 'Restoran'}, #${orderShortNo(o)}`;

/** Kuryenin restoranda bekleyen (kurye adına yola çıkarılabilir) paketleri. */
export const waitingAtRestaurant = (courier) => (courier?.orders || []).filter(isAtRestaurant);

/* ── Canlı kurye takibi: restoran panelindeki ekranla AYNI kurallar ─────────────────────── */

/** Atanmamış paketin rengi: kurye paletinde ve konum tazeliği renklerinde bilerek yok. */
export const UNASSIGNED_COLOR = '#06b6d4';
export const UNASSIGNED_DARK = '#0e7490';

/** Konum tazeliği: son bildirimin yaşına göre renk + kısa etiket. */
export function freshness(iso, nowMs = Date.now()) {
    const t = iso ? new Date(iso).getTime() : NaN;
    if (Number.isNaN(t)) return { color: '#9ca3af', label: 'Konum yok', cls: 'no-loc' };
    const min = (nowMs - t) / 60000;
    if (min < 5) return { color: '#10b981', label: 'Aktif', cls: 'active' };
    if (min < 15) return { color: '#f59e0b', label: 'Beklemede', cls: 'idle' };
    return { color: '#ef4444', label: 'Bağlantı kesildi', cls: 'disconnected' };
}

/** "Fiş 34"; fiş numarası henüz gelmediyse sipariş numarası ("#1042"). */
export function receiptLabel(o) {
    return o?.dailyOrderNo != null ? `Fiş ${o.dailyOrderNo}` : `#${orderShortNo(o)}`;
}

/** Paketin tam adı: filo birden çok restoran taşıdığı için restoran adı başta. "Köz Köze, Fiş 34" */
export const packageTitle = (o) => `${o?.partnerName || 'Restoran'}, ${receiptLabel(o)}`;

/** "Ahmet Y." — haritadaki dar balon için. */
export function shortCustomerName(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toLocaleUpperCase('tr')}.`;
}

/** Yan listede paketler fiş numarası sırasıyla görünür; özgün sıra (`i`) pin numarasıdır, korunur. */
export function byReceiptNo(orders) {
    return (orders || [])
        .map((o, i) => ({ o, i }))
        .sort((a, b) => (a.o.dailyOrderNo ?? Infinity) - (b.o.dailyOrderNo ?? Infinity) || a.i - b.i);
}

/** "2 paket (1 restoranda)" — restoranda bekleyen ayrıca yazılır ki hepsi kuryede sanılmasın. */
export function packageSummary(courier) {
    const orders = courier?.orders || [];
    const waiting = orders.filter(isAtRestaurant).length;
    return waiting > 0 ? `${orders.length} paket (${waiting} restoranda)` : `${orders.length} paket`;
}

/** Rota ve pin yeniden çizimi için imza: paket/statü/adres/fiş değişince değişir. */
export const ordersSignature = (courier) =>
    (courier?.orders || []).map((o) => `${o.deliveryId}:${o.statusCode}:${o.dropoffLat},${o.dropoffLng}:${o.dailyOrderNo ?? ''}`).join('|');

const COURIER_NAME_MAX = 20;
export function courierDisplayName(name) {
    const n = String(name || '').trim() || 'Kurye';
    return n.length > COURIER_NAME_MAX ? `${n.slice(0, COURIER_NAME_MAX - 1).trimEnd()}…` : n;
}

/** "7 sa 40 dk" / "25 dk" */
export function formatDuration(minutes) {
    const m = Math.max(0, Math.round(minutes || 0));
    const h = Math.floor(m / 60);
    return h > 0 ? `${h} sa ${m % 60} dk` : `${m} dk`;
}

/** "5 dk önce açıldı" için: "az önce" / "12 dk önce" / "2 sa önce". */
export function relativeTime(iso, now = new Date()) {
    return lastLocationAge(iso, now) || '';
}

/**
 * Sürüklenen paketin "kuryenin üstüne bırakıldı" sayılacağı en büyük ekran uzaklığı (piksel).
 * Kurye işaretinin üstündeki isim balonu da hedef sayılsın diye geniş.
 */
export const DROP_RADIUS_PX = 56;

/** Bırakılan ekran noktasına en yakın kurye; eşiğin dışındaysa null. `points`: [{ id, x, y }] */
export function nearestWithin(point, points, radius = DROP_RADIUS_PX) {
    let best = null;
    let bestDist = Infinity;
    (points || []).forEach((p) => {
        const d = Math.hypot(p.x - point.x, p.y - point.y);
        if (d < bestDist) { bestDist = d; best = p; }
    });
    return best && bestDist <= radius ? best.id : null;
}

/**
 * Rota etiketi: mesafe, süre, tahmini varış. Süre ham yol süresidir (trafik payı eklenmez),
 * iyimser olduğu "~" ile belli edilir. Yol servisi cevap vermediyse mesafe kuş uçuşudur ve yazar.
 */
export function formatRouteLabel({ distanceMeters, durationSeconds, isFallback, now }) {
    const km = distanceMeters / 1000;
    const core = km < 1
        ? `${Math.round(distanceMeters)} m`
        : `${km.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
    const distance = isFallback ? `~${core}` : core;
    // Süre ve varış saati AYNI yuvarlanmış dakikadan türer; yoksa etiket kendiyle çelişir.
    const etaMinutes = durationSeconds < 60 ? 0 : Math.round(durationSeconds / 60);
    let duration;
    if (durationSeconds < 60) duration = '<1 dk';
    else if (etaMinutes < 60) duration = `~${etaMinutes} dk`;
    else duration = `~${Math.floor(etaMinutes / 60)} sa ${etaMinutes % 60} dk`;
    const arrival = new Date(now + etaMinutes * 60000);
    const eta = `${pad(arrival.getHours())}:${pad(arrival.getMinutes())}`;
    return [distance, duration, eta, isFallback ? 'kuş uçuşu' : ''].filter(Boolean).join(', ');
}

/**
 * Kuryesiz bekleyen paketin açıklaması. Kod sunucudan gelir; burada sade bir cümleye çevrilir.
 * `assign: true` ise filonun atabileceği tek adım "Kurye ata"dır. Bilinmeyen kodda null.
 */
const WAIT_HINTS = {
    balance: { text: 'Restoranın bakiyesi bittiği için kurye atanamıyor.' },
    noCourierOnShift: { text: 'Şu an mesaide kuryeniz yok. Aşağıdaki listeden bir kuryeyi mesaiye başlatabilirsiniz.' },
    couriersPaused: { text: 'Mesaideki kuryelerinizin paket alımı durdurulmuş. Kurye satırından yeniden açabilirsiniz.' },
    manualAssign: { text: 'Bu paketi kuryeden siz aldınız; sistem kendiliğinden kurye seçmez.', assign: true },
    couriersBusy: { text: 'Şu an uygun kurye yok, boşalan ilk kuryeye verilecek.', assign: true },
};
export const waitHint = (reason) => (reason ? WAIT_HINTS[reason] || null : null);

/* ── "Mesaiyi bitir" penceresinde elle girilen saatler ──────────────────────────────────── */

const MANUAL_MAX_MS = 24 * 60 * 60 * 1000;
const MANUAL_FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

/** `<input type="datetime-local">` değeri (tarayıcının yerel saati). */
export function toLocalInputValue(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Girilenleri doğrular ve istek gövdesine çevirir. İkisi de boşsa gövde null (elle giriş yok).
 * Kurallar sunucudakiyle aynı: başlangıç bitişten önce, bitiş ileri bir saat değil, en fazla 24 saat.
 */
export function buildManualShiftTimes(startLocal, endLocal, now) {
    const s = String(startLocal || '').trim();
    const e = String(endLocal || '').trim();
    if (!s && !e) return { error: null, body: null };
    const start = s ? new Date(s) : null;
    const end = e ? new Date(e) : null;
    if ((start && Number.isNaN(start.getTime())) || (end && Number.isNaN(end.getTime()))) {
        return { error: 'Geçerli bir tarih ve saat girin.', body: null };
    }
    const effectiveEnd = end || now;
    if (effectiveEnd.getTime() > now.getTime() + MANUAL_FUTURE_TOLERANCE_MS) return { error: 'Bitiş ileri bir saat olamaz.', body: null };
    if (start && start.getTime() >= effectiveEnd.getTime()) return { error: 'Başlangıç bitişten önce olmalı.', body: null };
    if (start && effectiveEnd.getTime() - start.getTime() > MANUAL_MAX_MS) return { error: 'Mesai 24 saati aşamaz.', body: null };
    return {
        error: null,
        body: { reportedStartUtc: start ? start.toISOString() : null, reportedEndUtc: end ? end.toISOString() : null },
    };
}
